import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputMaskModule } from 'primeng/inputmask';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import {
  cnpjValidator,
  strongPasswordValidator,
} from 'src/app/shared/helper/validators';
import { FormErrorsComponent } from '../../../shared/components/form/form-errors/form-errors.component';
import { AUTH_CONFIG } from '../../config/auth-config';
import { IAuthService } from '../../services/iauth.service';
import { ContainerManagerLoginComponent } from '../container-manager-login/container-manager-login.component';
import { RegistroRequest } from './models/registro';
import { RegistroService } from './services/registro.service';

@Component({
  selector: 'app-novo-cadastro',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    ToastModule,
    ButtonModule,
    InputTextModule,
    InputMaskModule,
    PasswordModule,
    ProgressSpinnerModule,
    FormErrorsComponent,
    ContainerManagerLoginComponent,
  ],
  templateUrl: './novo-cadastro.component.html',
  styleUrl: './novo-cadastro.component.scss',
})
export class NovoCadastroComponent {
  private service = inject(RegistroService);
  private authService = inject(IAuthService);
  private formBuilder = inject(FormBuilder);
  private router = inject(Router);
  private config = inject(AUTH_CONFIG);

  loadding = false;
  progressMessage = '';
  form = this.createForm();

  private progressMessages = [
    'Criando sua conta...',
    'Preparando seu ambiente...',
    'Buscando informações da empresa...',
    'Finalizando configurações, por favor aguarde...'
  ];

  get senhaControl() {
    return this.form.get('senha');
  }

  get hasUpperCase() {
    const value = this.senhaControl?.value || '';
    return /[A-Z]/.test(value);
  }

  get hasLowerCase() {
    const value = this.senhaControl?.value || '';
    return /[a-z]/.test(value);
  }

  get hasNumeric() {
    const value = this.senhaControl?.value || '';
    return /[0-9]/.test(value);
  }

  get hasSpecialChar() {
    const value = this.senhaControl?.value || '';
    return /[!@#$%^&*(),.?":{}|<>]/.test(value);
  }

  get isValidLength() {
    const value = this.senhaControl?.value || '';
    return value.length >= 8;
  }

  onSubmit() {
    if (this.form.invalid) return;

    this.loadding = true;
    this.form.disable();
    this.startProgressMessages();
    var request = this.form.value as RegistroRequest;

    this.service
      .create(request)
      .subscribe({
        next: async () => {
          this.login(request.email, request.senha);
        },
      })
      .add(() => {
        this.loadding = false;
        this.form.enable();
        this.progressMessage = '';
      });
  }

  private startProgressMessages() {
    let currentIndex = 0;
    this.progressMessage = this.progressMessages[currentIndex];

    const interval = setInterval(() => {
      currentIndex++;
      if (currentIndex < this.progressMessages.length) {
        this.progressMessage = this.progressMessages[currentIndex];
      } else {
        clearInterval(interval);
      }
    }, 4000);
  }

  private login(login: string, senha: string) {
    this.authService
      .login(login, senha)
      .then(() => {
        const redirect = this.config.routes?.redirectAfterLogin || '/dashboard';
        this.router.navigate([redirect]);
        this.loadding = false;
      })
      .catch(() => {
        this.loadding = false;
        const loginRoute = this.config.routes?.loginRoute || 'login';
        this.router.navigate(['/', loginRoute]);
      });
  }

  private createForm() {
    return this.formBuilder.group({
      nome: new FormControl<string>('', {
        validators: [Validators.required, Validators.minLength(8)],
        nonNullable: true,
      }),
      email: new FormControl<string>('', {
        validators: [Validators.required, Validators.email],
        nonNullable: true,
      }),
      senha: new FormControl<string>('', {
        validators: [Validators.required, strongPasswordValidator()],
        nonNullable: true,
      }),
      cnpj: new FormControl<string>('', {
        validators: [Validators.required, cnpjValidator()],
        nonNullable: true,
      }),
      fone: new FormControl<string>('', {
        validators: [Validators.required],
        nonNullable: true,
      }),
    });
  }
}
